/* eslint-disable @repo/internal/react/require-jsdoc */
import React from 'react';

import { RichIcon, useOnHover } from '@af/design-system-docs-ui';
import Heading from '@atlaskit/heading';
import Lozenge from '@atlaskit/lozenge';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Box, Inline, Stack, Text, xcss } from '@atlaskit/primitives';
import { useThemeObserver } from '@atlaskit/tokens';

import { Card } from './card';
import type { ContentCardData } from './types';

const imageStyles = xcss({
	height: '160px',
	// background image dynamically set in `style` tag
	backgroundRepeat: 'no-repeat',
	backgroundSize: 'contain',
	backgroundPosition: '50% 50%',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
});

const imageStylesCover = xcss({
	backgroundSize: 'cover',
});

const statusMap = {
	'closed beta': 'new',
	beta: 'new',
	caution: 'moved',
	deprecated: 'removed',
	'early access': 'moved',
} as const;

export const ContentCard = ({
	image,
	icon,
	title,
	description,
	href,
	status,
	backgroundSize,
	externalIconSpacing,
}: ContentCardData) => {
	const { colorMode } = useThemeObserver();
	const [isHovered, hoverRef] = useOnHover<HTMLAnchorElement>();
	const shouldCover = backgroundSize && backgroundSize === 'cover';

	return (
		<Card
			key={href}
			href={href}
			borderColor="color.border"
			ref={hoverRef}
			externalIconSpacing={externalIconSpacing}
		>
			<Box
				backgroundColor="elevation.surface.sunken"
				style={{
					backgroundImage: image
						? `url(${typeof image === 'string' ? image : image[colorMode || 'light']})`
						: 'initial',
				}}
				xcss={[imageStyles, shouldCover && imageStylesCover]}
			>
				{icon && <RichIcon name={icon} shouldShowScribble={isHovered} />}
			</Box>

			<Box padding="space.200">
				<Stack space="space.050">
					<Inline spread="space-between">
						<Heading as="h3" size="small">
							{title}
						</Heading>
						{status && <Lozenge appearance={statusMap[status]}>{status}</Lozenge>}
					</Inline>
					<Text>{description}</Text>
				</Stack>
			</Box>
		</Card>
	);
};
